import React, { useMemo, useState } from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router';
import {
  HOMEPAGE_LINK_ALT_TEXT,
  SEARCH_INPUT_PLACEHOLDER,
} from '../../../assets/copy';
import { ReactComponent as Logo } from '../../../assets/icons/logo.svg';
import { trackClick, trackSiteSearch } from '../../../utils/useOneTag';
import MarketSelector from '../MarketSelector';
import MenuAnimation from '../../animation/MenuAnimation';
import SearchOverlay from '../../../pages/search/SearchOverlay';
import SearchOverlayRefresh from '../../../pages/search-refresh/SearchOverlay';
import SearchButton, { SearchBarButton } from '../../core/AppBar/SearchButton';
import UserNavigationDropdown from '../../core/AppBar/UserNavigationDropdown';
import { DESKTOP_NAVIGATION_LINKS } from '../../../utils/constants/Links';
import NavigationLink from '../../core/NavigationLink';
import MobileMenuButton from '../../core/AppBar/MobileMenuButton';
import { useDispatch, useSelector } from 'react-redux';
import { actionSetNavigationMenuVisible } from '../../../store/AppData/AppDataAction';
import MobileMenu from '../../core/AppBar/MobileMenu';
import { useViewport } from '../../../utils/useViewport';
import { BREAKPOINT_VALUES } from '../../../utils/constants/Breakpoints';
import clsx from 'clsx';
import { focusVisible } from '../../../utils/ada/contrastStyles';
import { NAVIGATION_PREMIER_LOCATIONS } from '../../../assets/copy';
import { useFeatureFlags } from '../../../utils/useFeatureFlags';
import {
  useCuisineAndRestaurantSearch,
  DEBOUNCE_MILLISECONDS,
  SEARCH_TRACK_MAX_LENGTH,
} from '../Search/useCuisineAndRestaurantSearch';
import SearchBar from '../Search/SearchBar';
import { Autocomplete } from '../Search/Autocomplete';
import { debounce } from 'lodash';

const DESKTOP_BREAKPOINT = BREAKPOINT_VALUES.xxl;
const TABLET_BREAKPOINT = BREAKPOINT_VALUES.lg;

const useStyles = makeStyles((theme) => ({
  root: {},
  header: {
    width: '100%',
    height: ({ variant }) => (variant === 'compact' ? 54 : 96),
    backgroundColor: theme.palette.background.white,
  },
  responsiveHeader: {
    height: '48px !important',
    [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
      height: '62px !important',
    },
    [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
      height: '96px !important',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: theme.breakpoints.values.xxl,
    margin: '0 auto',
    padding: theme.spacing(0, 1),
    height: '100%',
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(3),
      padding: theme.spacing(0, 3),
    },
  },
  logoButton: {
    ...focusVisible(),
    boxSizing: 'border-box',
    padding: theme.spacing(1),
    borderRadius: 2,
  },
  logo: { width: 105, minWidth: 105 },
  section: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  navLinkGap: {
    gap: theme.spacing(2),
    [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
      gap: theme.spacing(4),
    },
  },
  searchBar: {
    '& .MuiFormControl-root, #navbar-search-popup': {
      maxWidth: theme.spacing(37),
    },
    [theme.breakpoints.down(DESKTOP_BREAKPOINT)]: {
      '& .MuiFormControl-root, #navbar-search-popup': {
        maxWidth: theme.spacing(32),
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1, 0),
      },
    },
  },
}));

const BREAKPOINT = BREAKPOINT_VALUES.xxl;

const NavSearchBar = () => {
  const classes = useStyles();
  const onInputChangeHandler = (e, v, reason) => {
    if (reason === 'reset' || reason === 'clear') {
      setInputValue('');
    } else if (reason === 'input') {
      setInputValue(v);
    }
  };
  const [inputValue, setInputValue] = useState('');

  const {
    getRootProps,
    getInputProps,
    getClearProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useCuisineAndRestaurantSearch({
    id: 'navbar-search',
    inputValue,
    onInputChange: onInputChangeHandler,
  });
  const { market } = useSelector((state) => state.markets);
  const debouncedChangeHandler = useMemo(
    () => debounce((input) => fetchQuery(input), DEBOUNCE_MILLISECONDS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getInputPropsClone = () => ({
    ...getInputProps(),
    onKeyDown: debouncedChangeHandler,
  });

  const fetchQuery = (e) => {
    const keyword = e.target.value;
    if (keyword.length >= SEARCH_TRACK_MAX_LENGTH) {
      trackSiteSearch(
        keyword,
        `Search revamp - not submitted user input - Market ID: ${market?.id}`
      );
    }
  };

  const trackClickOnNavSearcBar = (e) => {
    trackClick(e, 'search-revamp-app-bar-search', 'Button');
  };

  return (
    <div
      {...getRootProps()}
      className={classes.searchBar}
      data-cy="search-revamp-app-bar-search"
      onClick={trackClickOnNavSearcBar}>
      <SearchBar
        getInputProps={getInputPropsClone}
        getClearProps={getClearProps}
        placeholder={SEARCH_INPUT_PLACEHOLDER}
      />
      <Autocomplete
        groupedOptions={groupedOptions}
        getListboxProps={getListboxProps}
        getOptionProps={getOptionProps}
        getInputProps={getInputProps}
        showSearchButton
      />
    </div>
  );
};

/**
 * Main app navigation bar
 *
 * @param {object} props
 * @param {object} [props.classes] Overrides or extends default styles
 * @param {'default'|'compact'} [props.variant] The variant of the navigation bar ('compact' reduces height to 54px) TODO: Remove after search revamp
 * @returns {JSX.Element}
 */
export const NavBar = ({ classes: classesOverride, variant = 'default' }) => {
  const classes = useStyles({ classes: classesOverride, variant });
  const history = useHistory();
  const { width } = useViewport();
  const dispatch = useDispatch();
  const { overlayVisible } = useSelector((state) => state.search);
  const [menuVisible, setMenuVisible] = useState(false);
  const { refreshSearchRevampFlagIsEnabled } = useFeatureFlags();

  const handleClick = (event) => {
    trackClick(event, 'app-bar-logo', 'Link');
    history.push('/');
  };
  const handleNavLinkClick = (event, dataCy) => {
    trackClick(event, dataCy, 'Link');
  };
  const toggleMenu = (shouldShowMenu = !menuVisible) => {
    setMenuVisible(shouldShowMenu || !menuVisible);
    dispatch(actionSetNavigationMenuVisible(shouldShowMenu || !menuVisible));
  };

  const showNavLinks = useMemo(() => {
    return refreshSearchRevampFlagIsEnabled
      ? width > DESKTOP_BREAKPOINT
      : variant === 'default';
  }, [refreshSearchRevampFlagIsEnabled, variant, width]);

  return (
    <div className={classes.root}>
      <header
        className={clsx(classes.header, {
          [classes.responsiveHeader]: refreshSearchRevampFlagIsEnabled,
        })}>
        <div className={classes.container}>
          <div className={clsx(classes.section, classes.navLinkGap)}>
            <Button
              disableRipple
              className={classes.logoButton}
              onClick={handleClick}
              data-cy="app-bar-logo"
              aria-label={HOMEPAGE_LINK_ALT_TEXT}>
              <Logo className={classes.logo} />
            </Button>
            {showNavLinks &&
              DESKTOP_NAVIGATION_LINKS.map(
                ({ label, path, dataCy }) =>
                  label !== NAVIGATION_PREMIER_LOCATIONS && (
                    <NavigationLink
                      key={path}
                      label={label}
                      path={path}
                      variant={'small2Normal'}
                      data-cy={dataCy}
                      onClick={(e) => {
                        handleNavLinkClick(e, dataCy);
                      }}
                    />
                  )
              )}
          </div>

          {refreshSearchRevampFlagIsEnabled ? (
            <div
              className={clsx(classes.section, {
                [classes.navLinkGap]: width > TABLET_BREAKPOINT,
              })}>
              {!overlayVisible && <MarketSelector />}

              {width < TABLET_BREAKPOINT ? (
                <SearchButton menuVisible={menuVisible || overlayVisible} />
              ) : (
                <NavSearchBar />
              )}

              {width < DESKTOP_BREAKPOINT ? (
                <MobileMenuButton
                  handlePress={() => toggleMenu(true)}
                  menuVisible={menuVisible}
                />
              ) : (
                <UserNavigationDropdown />
              )}
            </div>
          ) : (
            <div
              className={clsx(classes.section, {
                [classes.navLinkGap]: width > BREAKPOINT,
              })}>
              {!overlayVisible && <MarketSelector />}
              {variant === 'compact' || width < BREAKPOINT ? (
                <SearchButton menuVisible={menuVisible} />
              ) : (
                <SearchBarButton menuVisible={menuVisible} />
              )}

              {variant === 'compact' ? (
                <MobileMenuButton
                  handlePress={() => toggleMenu(true)}
                  menuVisible={menuVisible}
                />
              ) : (
                <UserNavigationDropdown />
              )}
            </div>
          )}
        </div>
      </header>
      <MenuAnimation isOpen={menuVisible}>
        <MobileMenu close={() => toggleMenu(false)} isOpen={menuVisible} />
      </MenuAnimation>
      {!menuVisible && (
        <MenuAnimation isOpen={overlayVisible}>
          {refreshSearchRevampFlagIsEnabled ? (
            <SearchOverlayRefresh />
          ) : (
            <SearchOverlay />
          )}
        </MenuAnimation>
      )}
    </div>
  );
};

export default NavBar;
